/**
 * components/UploadReminderTile.js — dashboard tile.
 *
 * Rendered when useUploadReminder(snapshot) returns a non-null reminder.
 * Not dismissible (per product decision) — always visible while any of
 * the three trigger conditions holds. Once the user uploads or adds a
 * transaction, the snapshot's `last_txn_date` refreshes and the tile
 * naturally goes away.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Clock, Calendar } from 'lucide-react';

const ICONS = {
  month_end: Calendar,
  new_month: Calendar,
  stale:     Clock,
};

export function UploadReminderTile({ reminder }) {
  if (!reminder) return null;
  const Icon = ICONS[reminder.reason] || Upload;

  return (
    <Card className="rounded-2xl border-primary/30 bg-primary/[0.03]" data-testid="upload-reminder-tile">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 bg-primary text-primary-foreground">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium" data-testid="upload-reminder-title">
              {reminder.title}
            </div>
            <div className="text-xs text-muted-foreground mt-1 leading-relaxed" data-testid="upload-reminder-message">
              {reminder.message}
            </div>
            <div className="mt-3">
              <Button asChild size="sm" className="rounded-xl" data-testid="upload-reminder-cta">
                <Link to={reminder.cta_href}>
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  {reminder.cta}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default UploadReminderTile;
